import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Mail, Phone, MapPin, Send, Instagram, Twitter, Facebook } from "lucide-react";
import { useToast } from "@/hooks/useToast";
import { Toast } from '@/components/ui/ToastProvider';

const Contact = () => {
  const { open, setOpen, data, toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch("https://formspree.io/f/mzzrbwer", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast({
          title: "Success!",
          description: "Thank you for reaching out. I'll get back to you soon."
        });
        setFormData({ name: "", email: "", message: "" });
      } else {
        throw new Error("Failed to send message");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again later."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="page-container">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gradient mb-8">
              Contact Me
            </h1>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6 order-2 md:order-1">
                <div className="glass-card p-4 md:p-6">
                  <h2 className="text-xl md:text-2xl font-semibold mb-4">Contact Information</h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 md:w-10 h-8 md:h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Mail className="w-4 md:w-5 h-4 md:h-5 text-primary" />
                      </div>
                      <span className="text-sm md:text-base">contact@sofimileroy.com</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 md:w-10 h-8 md:h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Phone className="w-4 md:w-5 h-4 md:h-5 text-primary" />
                      </div>
                      <span className="text-sm md:text-base">+380 667444946</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 md:w-10 h-8 md:h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <MapPin className="w-4 md:w-5 h-4 md:h-5 text-primary" />
                      </div>
                      <span className="text-sm md:text-base">Lviv, Ukraine</span>
                    </div>
                  </div>
                </div>

                <div className="glass-card p-4 md:p-6">
                  <h2 className="text-xl md:text-2xl font-semibold mb-4">Social Media</h2>
                  <div className="flex flex-wrap gap-4">
                    <a href="https://www.instagram.com/sofi.mileroy/" target="_blank" rel="noopener noreferrer">
                      <Instagram className="w-8 h-8 text-foreground" strokeWidth={1.5} />
                    </a>
                    <a href="https://www.tiktok.com/@sofi.mileroy" target="_blank" rel="noopener noreferrer">
                      <svg className="w-8 h-8 text-foreground" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                      </svg>
                    </a>
                    <a href="https://x.com/SofiMileroy" target="_blank" rel="noopener noreferrer">
                      <Twitter className="w-8 h-8 text-foreground" strokeWidth={1.5} />
                    </a>
                    <a href="#" target="_blank" rel="noopener noreferrer">
                      <Facebook className="w-8 h-8 text-foreground" strokeWidth={1.5} />
                    </a>
                    <a href="https://t.me/Afk_afk" target="_blank" rel="noopener noreferrer">
                      <svg className="w-8 h-8 text-foreground" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.665 3.717l-17.73 6.837c-1.21.486-1.203 1.161-.222 1.462l4.552 1.42l10.532-6.645c.498-.303.953-.14.579.192l-8.533 7.701l-.332 4.865c.487 0 .703-.22.975-.48l2.34-2.272l4.866 3.596c.897.494 1.54.24 1.764-.83l3.177-14.995c.255-1.025-.38-1.492-1.343-1.087z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>

              <div className="glass-card p-4 md:p-6 order-1 md:order-2">
                <h2 className="text-xl md:text-2xl font-semibold mb-4">Send Message</h2>
                
                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div>
                    <label className="block text-sm font-medium mb-2">Name</label>
                    <input
                      type="text"
                      className="w-full p-2 md:p-3 rounded-md border bg-background text-sm md:text-base"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Email</label>
                    <input
                      type="email"
                      className="w-full p-2 md:p-3 rounded-md border bg-background text-sm md:text-base"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Message</label>
                    <textarea
                      className="w-full p-2 md:p-3 rounded-md border bg-background h-24 md:h-32 text-sm md:text-base"
                      placeholder="Your message..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      disabled={isSubmitting}
                    ></textarea>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full flex items-center justify-center gap-2 py-2 md:py-3"
                    disabled={isSubmitting}
                  >
                    <Send className="w-4 h-4" />
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <Toast 
        open={open} 
        onOpenChange={setOpen}
        title={data.title}
        description={data.description}
      />
    </>
  );
};

export default Contact;
